import { c as _c } from "react/compiler-runtime";
import { Avatar, Box, Button, Flex, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { UserSignupEmailCaptureForm } from '@src/components/sco/Account/UserSignupEmailCaptureForm';
import { ClarityAdvisorImportHoldDialog } from '@src/components/sco/Advisors/sections/ClarityAdvisorImportHoldDialog';
import { type ClarityAdvisorImportHoldPhase } from '@src/components/sco/Advisors/sections/clarityAdvisorImportHold';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { TextLink } from '@src/components/sco/TextLink';
import { ScoClarityAdvisorImportClaimStatus } from '@src/graphql-types';
import { ReactNode } from 'react';
export type ClarityAdvisorImportExpert = {
  fullName?: string | null;
  firstName?: string | null;
  screenName?: string | null;
  bio?: string | null;
  location?: string | null;
  imageUrl?: string | null;
  hourlyRate?: number | null;
  clarityProfileUrl?: string | null;
  shortBio?: string | null;
};
export type ClarityAdvisorImportViewProps = {
  handle: string;
  claimStatus: ScoClarityAdvisorImportClaimStatus;
  expert: ClarityAdvisorImportExpert | null;
  isRegistered: boolean;
  claiming?: boolean;
  extracting?: boolean;
  holdPhase?: ClarityAdvisorImportHoldPhase;
  error?: string | null;
  loginHref: string;
  applyHref: string;
  becomeHref: string;
  onConfirm?: () => void;
  /** Override guest signup (Storybook). Production omits this. */
  guestSignup?: ReactNode;
  /** Compact layout for the landing diagnostic card (no page chrome). */
  embedded?: boolean;
};
const rateLabel = (rate?: number | null) => {
  if (rate == null || Number.isNaN(Number(rate))) {
    return null;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Math.round(Number(rate)));
};
const PageShell = t0 => {
  const $ = _c(5);
  const {
    children
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [10, 12];
    t2 = [8, 10];
    t3 = [5, 8, 10];
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <Flex align="center" direction="column" justify="center" minHeight="full" paddingBottom={t1} paddingTop={t2} paddingX={t3} width="full"><Box maxWidth="lg" width="full">{children}</Box></Flex>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const EmbeddedShell = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Box textAlign="left" width="full">{children}</Box>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const ClarityAdvisorImportView = t0 => {
  const $ = _c(79);
  const {
    handle,
    claimStatus,
    expert,
    isRegistered,
    claiming: t1,
    extracting: t2,
    holdPhase,
    error: t3,
    loginHref,
    applyHref,
    becomeHref,
    onConfirm,
    guestSignup,
    embedded: t4
  } = t0;
  const claiming = t1 === undefined ? false : t1;
  const extracting = t2 === undefined ? false : t2;
  const error = t3 === undefined ? null : t3;
  const embedded = t4 === undefined ? false : t4;
  const phase = holdPhase || (extracting ? "extracting" : claiming ? "claiming" : "idle");
  const busy = phase !== "idle";
  let t5;
  if ($[0] !== expert?.firstName) {
    t5 = expert?.firstName?.trim();
    $[0] = expert?.firstName;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const firstName = t5;
  const name = expert?.fullName || firstName || handle;
  const headingName = firstName || name;
  let t6;
  if ($[2] !== expert?.shortBio) {
    const shortBio = expert?.shortBio?.trim();
    t6 = shortBio && shortBio.toLowerCase() !== "clarity expert" ? shortBio : null;
    $[2] = expert?.shortBio;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const blurb = t6;
  const t7 = expert?.hourlyRate;
  let t8;
  if ($[4] !== t7) {
    t8 = rateLabel(t7);
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const rate = t8;
  const Shell = embedded ? EmbeddedShell : PageShell;
  if (claimStatus === ScoClarityAdvisorImportClaimStatus.NotFound || claimStatus === ScoClarityAdvisorImportClaimStatus.NotExpert) {
    let t9;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Heading fontSize={["2xl", "3xl"]} letterSpacing="-0.03em">We couldn't find that profile</Heading>;
      $[6] = t9;
    } else {
      t9 = $[6];
    }
    const t10 = claimStatus === ScoClarityAdvisorImportClaimStatus.NotExpert ? "That Clarity account isn't set up as an expert." : "There's no Clarity expert at this link.";
    let t11;
    if ($[7] !== t10) {
      t11 = <VStack align="stretch" gap={2}>{t9}<Text color="fg.muted" fontFamily="longform" textStyle="md">{t10}{" "}You can still apply to advise on Startups.com.</Text></VStack>;
      $[7] = t10;
      $[8] = t11;
    } else {
      t11 = $[8];
    }
    let t12;
    if ($[9] !== becomeHref) {
      t12 = <ButtonLink href={becomeHref} variant="cta" width="full">Apply to advise</ButtonLink>;
      $[9] = becomeHref;
      $[10] = t12;
    } else {
      t12 = $[10];
    }
    let t13;
    if ($[11] !== t11 || $[12] !== t12) {
      t13 = <VStack align="stretch" gap={5}>{t11}{t12}</VStack>;
      $[11] = t11;
      $[12] = t12;
      $[13] = t13;
    } else {
      t13 = $[13];
    }
    let t14;
    if ($[14] !== Shell || $[15] !== t13) {
      t14 = <Shell>{t13}</Shell>;
      $[14] = Shell;
      $[15] = t13;
      $[16] = t14;
    } else {
      t14 = $[16];
    }
    return t14;
  }
  const t9 = embedded ? 4 : 6;
  let t10;
  if ($[17] !== embedded) {
    t10 = embedded ? ["xl", "2xl"] : ["2xl", "3xl"];
    $[17] = embedded;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = claimStatus === ScoClarityAdvisorImportClaimStatus.ClaimedByMe ? `Welcome back, ${headingName}` : `${headingName}, is this you?`;
  let t12;
  if ($[19] !== t10 || $[20] !== t11) {
    t12 = <Heading fontSize={t10} letterSpacing="-0.03em">{t11}</Heading>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = claimStatus === ScoClarityAdvisorImportClaimStatus.ClaimedByMe ? "Finish your advisor application whenever you're ready." : "We'll start your application from this profile.";
  let t14;
  if ($[22] !== t13) {
    t14 = <Text color="fg.muted" fontFamily="longform" textStyle="md">{t13}</Text>;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t12 || $[25] !== t14 || $[26] !== t9) {
    t15 = <VStack align="stretch" gap={2} marginBottom={t9}>{t12}{t14}</VStack>;
    $[24] = t12;
    $[25] = t14;
    $[26] = t9;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== embedded) {
    t16 = embedded ? 6 : [8, 10];
    $[28] = embedded;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== name) {
    t17 = <Avatar.Fallback name={name} />;
    $[30] = name;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== expert || $[33] !== name) {
    t18 = expert?.imageUrl ? <Avatar.Image alt={name} src={expert.imageUrl} /> : null;
    $[32] = expert;
    $[33] = name;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t17 || $[36] !== t18) {
    t19 = <Avatar.Root flexShrink={0} size="xl">{t17}{t18}</Avatar.Root>;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== name) {
    t20 = <Text fontWeight="semibold">{name}</Text>;
    $[38] = name;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== expert) {
    t21 = expert?.location ? <Text color="fg.muted" fontSize="sm">{expert.location}</Text> : null;
    $[40] = expert;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== blurb) {
    t22 = blurb ? <Text color="fg.muted" fontSize="sm">{blurb}</Text> : null;
    $[42] = blurb;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== rate) {
    t23 = rate ? <Text color="fg.muted" fontSize="sm">{rate}/hr on Clarity</Text> : null;
    $[44] = rate;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== expert) {
    t24 = expert?.clarityProfileUrl ? <TextLink href={expert.clarityProfileUrl} target="_blank" width="fit-content">View on Clarity.fm</TextLink> : null;
    $[46] = expert;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t20 || $[49] !== t21 || $[50] !== t22 || $[51] !== t23 || $[52] !== t24) {
    t25 = <VStack align="flex-start" gap={1} minWidth={0}>{t20}{t21}{t22}{t23}{t24}</VStack>;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t16 || $[55] !== t19 || $[56] !== t25) {
    t26 = <HStack align="start" gap={4} marginBottom={t16}>{t19}{t25}</HStack>;
    $[54] = t16;
    $[55] = t19;
    $[56] = t25;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== error) {
    t27 = error ? <Text color="red.fg" fontSize="sm" marginBottom={4}>{error}</Text> : null;
    $[58] = error;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== applyHref || $[61] !== busy || $[62] !== claimStatus || $[63] !== guestSignup || $[64] !== isRegistered || $[65] !== loginHref || $[66] !== onConfirm) {
    t28 = claimStatus === ScoClarityAdvisorImportClaimStatus.ClaimedByMe ? <ButtonLink href={applyHref} variant="cta" width="full">Continue your application</ButtonLink> : isRegistered ? <Button disabled={busy || !onConfirm} loading={busy} variant="cta" width="full" onClick={onConfirm}>Continue with this profile</Button> : <VStack align="stretch" gap={4}>{guestSignup ?? <UserSignupEmailCaptureForm brand="clarity" hubspotFormKey="scom-advisors-clarity-import" socialLoginButtonsProps={{
        buttonLayout: "google-only"
      }} submitButtonProps={{
        children: "Create account",
        size: "lg"
      }} containerProps={{
        bg: "transparent",
        borderWidth: "0",
        boxShadow: "none",
        padding: 0,
        width: "full"
      }} />}<Text color="fg.muted" fontSize="sm" textAlign="center">Already have an account? <TextLink href={loginHref}>Log in</TextLink></Text></VStack>;
    $[60] = applyHref;
    $[61] = busy;
    $[62] = claimStatus;
    $[63] = guestSignup;
    $[64] = isRegistered;
    $[65] = loginHref;
    $[66] = onConfirm;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== phase) {
    t29 = <ClarityAdvisorImportHoldDialog phase={phase} />;
    $[68] = phase;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t15 || $[71] !== t26 || $[72] !== t27 || $[73] !== t28 || $[74] !== t29) {
    t30 = <VStack align="stretch" gap={0}>{t15}{t26}{t27}{t28}{t29}</VStack>;
    $[70] = t15;
    $[71] = t26;
    $[72] = t27;
    $[73] = t28;
    $[74] = t29;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== Shell || $[77] !== t30) {
    t31 = <Shell>{t30}</Shell>;
    $[76] = Shell;
    $[77] = t30;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  return t31;
};